import {Alert, Avatar, Button, Card, openDialog, Separator} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {useTheme} from 'styled-components';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {CustomerMessageResponseBody, SecondaryRecordId} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import {i18n} from '@dms/i18n';

import {parseDate} from 'shared/web';

import {SkeletonLoadingBox} from '../../EntityHeader/components/SkeletonLoadingBox';
import {SendCustomerEmailForm} from '../../SendCustomerEmailForm/SendCustomerEmailForm';
import {ReplyTarget} from '../../SendCustomerEmailForm/types/ReplyTarget';
import {useThreadEmailHeader} from '../hooks/useThreadEmailHeader';
import {MessageContextLink} from '../types/MessageContextLink';
import {ThreadEmailSize} from '../types/ThreadEmailSize';
import {ThreadReadState} from '../types/ThreadReadState';
import {isMessageContextResource} from '../utils/getMessageContextLinks';
import {getThreadEmailLayout} from '../utils/getThreadEmailLayout';
import {isCustomerMessageDeliveryError} from '../utils/isCustomerMessageDeliveryError';
import {ThreadContextLinks} from './ThreadContextLinks';
import {ThreadEmailAttachment} from './ThreadEmailAttachment';
import {ThreadEmailWindow} from './ThreadEmailWindow';
import {ThreadInReplyToChip} from './ThreadInReplyToChip';
import {ThreadReadStateControls} from './ThreadReadStateControls';

interface ThreadEmailCardProps {
  message: CustomerMessageResponseBody;
  isBasicMessagingOnly?: boolean;
  contextLinks: MessageContextLink[];
  parentMessage: CustomerMessageResponseBody | null;
  isHighlighted: boolean;
  anchorId: string;
  onParentChipClick: (parentMessageId: string) => void;
  showOutgoingRecipient?: boolean;
  /** Enables the "new" marker and the manual read toggle; without it the card shows neither. */
  readState?: ThreadReadState;
}

const getMessageRecord = (message: CustomerMessageResponseBody, customerId: string) =>
  isNotNilOrEmpty(message.recordId) &&
  (message.resourceId === EntityResourceIds.directSale ||
    isMessageContextResource(message.resourceId))
    ? {recordId: message.recordId, resourceId: message.resourceId}
    : {recordId: customerId, resourceId: EntityResourceIds.customer};

const isEntityResourceId = (value: string): value is EntityResourceIds =>
  Object.values(EntityResourceIds).some((id) => id === value);

const toSecondaryRecordIds = (
  records: CustomerMessageResponseBody['secondaryRecords']
): SecondaryRecordId[] =>
  records.flatMap((record) =>
    isEntityResourceId(record.resourceId)
      ? [{recordId: record.recordId, resourceId: record.resourceId}]
      : []
  );

export function ThreadEmailCard(props: ThreadEmailCardProps) {
  const message = props.message;
  const formatDateTime = useDateTimeFormatter();
  const theme = useTheme();

  const handleParentChipClick = () => {
    if (isNil(props.parentMessage)) {
      return;
    }
    props.onParentChipClick(props.parentMessage.id);
  };

  const [emailSize, setEmailSize] = useState<ThreadEmailSize | null>(null);

  const {isIncoming, headerAddress, isSenderLoading} = useThreadEmailHeader(
    message,
    props.showOutgoingRecipient
  );

  const isDeliveryError = isCustomerMessageDeliveryError(message.state);
  const colorStripe = match<
    {isDeliveryError: boolean; isIncoming: boolean},
    'red' | 'blue' | 'green'
  >({isDeliveryError, isIncoming})
    .with({isDeliveryError: true}, () => 'red')
    .with({isIncoming: true}, () => 'blue')
    .otherwise(() => 'green');

  const layout = getThreadEmailLayout(emailSize);

  const canReply = isNotNil(message.emailMessageId) && isNotNilOrEmpty(message.customerId);
  const handleReply = () => {
    if (isNil(message.customerId) || isNil(message.emailMessageId)) {
      return;
    }
    const record = getMessageRecord(message, message.customerId);
    const replyTarget: ReplyTarget = {
      emailMessageId: message.emailMessageId,
      direction: message.direction,
      senderAddress: message.senderAddress,
      recipientAddresses: message.recipientAddresses,
      parentMessage: {
        messageId: message.id,
        subject: message.subject,
        from: message.contact,
        receivedAt: message.createdAt,
      },
    };
    openDialog(
      <SendCustomerEmailForm
        isBasicMessagingOnly={props.isBasicMessagingOnly}
        customerId={message.customerId}
        recordId={record.recordId}
        resourceId={record.resourceId}
        secondaryRecordIds={toSecondaryRecordIds(message.secondaryRecords)}
        reply={replyTarget}
      />,
      {
        withAdditionalFooter: true,
        size: 'large',
        title: i18n.t('entity.customer.labels.replyToCustomerMessageTitle'),
      }
    );
  };

  // The iframe must stay mounted and laid out to be measured, so instead of withholding the card we
  // keep it in the flow but invisible (opacity) until the first measurement lands, then reveal it at
  // its correct width/height — this hides the fallback-size → measured-size flash on first render.
  const isMeasured = isNotNil(emailSize);

  return (
    <HStack width="100%" justify={isIncoming ? 'flex-start' : 'flex-end'}>
      <Box
        id={props.anchorId}
        border="2px solid"
        borderRadius={theme.components.Card.borderRadius}
        borderColor={props.isHighlighted ? 'palettes.blue.60.100' : 'general.transparent'}
        data-testid={props.isHighlighted ? 'communicationThread-highlighted' : undefined}
        width={layout.cardWidthUnits}
        minWidth="60%"
        maxWidth={layout.cardMaxWidth}
        opacity={isMeasured ? 1 : 0}
      >
        <Card colorStripe={colorStripe}>
          <VStack spacing={3} align="flex-start">
            <HStack width="100%" spacing={3} align="center" justify="space-between" wrap>
              <HStack spacing={2} align="center">
                <Avatar
                  icon="communication/email"
                  color={isIncoming ? 'palettes.blue.60.100' : 'palettes.green.60.100'}
                  iconColor="general.white"
                  size="small"
                />
                <VStack>
                  <Show when={isNotNilOrEmpty(message.subject)}>
                    <Text size="small" alternative>
                      {message.subject}
                    </Text>
                  </Show>
                  <SkeletonLoadingBox isLoading={isSenderLoading} width={45} height={4}>
                    <Text size="xSmall" color="tertiary">
                      {headerAddress}
                    </Text>
                  </SkeletonLoadingBox>
                </VStack>
              </HStack>
              <HStack spacing={2} align="center" wrap>
                <ThreadReadStateControls message={message} readState={props.readState} />

                <Text size="xSmall" color="tertiary" noWrap>
                  {formatDateTime('dateTimeShort', parseDate(message.createdAt))}
                </Text>

                <Show when={isNotNil(message.parentEmailMessageId)}>
                  <ThreadInReplyToChip
                    parentMessage={props.parentMessage}
                    onClick={handleParentChipClick}
                  />
                </Show>

                <Show when={isNotNilOrEmpty(props.contextLinks)}>
                  <ThreadContextLinks links={props.contextLinks} />
                </Show>

                <Show when={canReply}>
                  <Button
                    variant="secondary"
                    size="small"
                    leftIcon="content/reply"
                    title={i18n.t('general.actions.reply')}
                    onClick={handleReply}
                    data-testid="communicationThread-reply"
                  />
                </Show>
              </HStack>
            </HStack>
            <Separator spacing={0} />

            <Show when={isDeliveryError}>
              <Alert
                type="inline"
                variant="error"
                title={i18n.t('general.labels.messageNotDelivered')}
                data-testid="communicationThread-emailDeliveryError"
              />
            </Show>

            <Box width="100%">
              <ThreadEmailWindow
                htmlData={message.content}
                onMeasure={setEmailSize}
                data-testid="communicationThread-emailWindow"
              />
            </Box>

            <Show when={isNotNilOrEmpty(message.attachments)}>
              <VStack spacing={1} align="flex-start" wrap>
                {message.attachments.map((attachment) => (
                  <ThreadEmailAttachment
                    key={attachment.uri}
                    attachment={attachment}
                    data-testid="communicationThread-emailAttachment"
                  />
                ))}
              </VStack>
            </Show>
          </VStack>
        </Card>
      </Box>
    </HStack>
  );
}
